import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import Title from 'components/Title';
import Layout from 'components/Layout';
import { useQuery, useConfirm } from 'hooks';

import { Box, Grid, Paper, Button, Container, IconButton } from '@material-ui/core';
import Can from 'components/Can';
import { debounce } from 'lodash';
import { Table } from 'components/UI';
import { toQueryString } from 'utils';
import Switch from '@material-ui/core/Switch';
import EditIcon from '@material-ui/icons/Edit';
import Tooltip from '@material-ui/core/Tooltip';
import SearchIcon from '@material-ui/icons/Search';
import { SearchField } from 'components/FormFields';
import ArchiveIcon from '@material-ui/icons/Archive';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { useStyles, tableStyles } from './List.styled';
import UnarchiveIcon from '@material-ui/icons/Unarchive';
import InputAdornment from '@material-ui/core/InputAdornment';
import { ReservationsActions } from 'state/modules/reservations';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { BusinessHoursActions } from 'state/modules/businessHours';

const BusinessHoursList = () => {
  const classes = useStyles();
  const urlParams = useQuery();
  const dispatch = useDispatch();
  const history = useHistory();
  const isMounted = useRef(false);
  const abortController = useRef(null);
  const businessHours = useSelector((state) => state.reservations.businessHours);
  const pagination = useSelector((state) => state.reservations.pagination);
  const fetchingBusinessHours = useSelector((state) => state.reservations.fetchingBusinessHours);
  const fetchingUpdateBH = useSelector((state) => state.businessHours.fetchingUpdateBH);
  const fetchedUpdateBH = useSelector((state) => state.businessHours.fetchedUpdateBH);

  const [currentBusinessHour, setCurrentBusinessHour] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    archived: false,
    ...urlParams,
  });

  /**
   * Get business hours list
   * @param {object} params Request params
   */
  const fetchBusinessHours = useCallback(
    (params = {}) => {
      const _params = { ...params };
      if (params.archived) {
        delete _params.archived;
      } else {
        _params.archived = false;
      }
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        ReservationsActions.fetchBusinessHours(_params, {
          signal: abortController?.current?.signal,
        })
      );
    },
    [dispatch]
  );

  const fetchBusinessHoursDebounced = useCallback(
    debounce((params) => fetchBusinessHours(params), 1000),
    [fetchBusinessHours]
  );

  const [toggleArchiveBusinessHours, ArchiveBusinessHoursConfirm] = useConfirm({
    title: i18n.t(
      `business_hours.${currentBusinessHour?.enabled ? 'archive_title' : 'activate_title'}`
    ),
    message: i18n.t(
      `business_hours.${currentBusinessHour?.enabled ? 'archive_confirm' : 'activate_confirm'}`
    ),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t(
      i18n.t(`business_hours.${currentBusinessHour?.enabled ? 'archive' : 'activate'}`)
    ),
    onConfirm: useCallback(() => {
      dispatch(
        BusinessHoursActions.fetchUpdateBusinessHours(currentBusinessHour.id, {
          archived: currentBusinessHour.enabled,
        })
      );
    }, [currentBusinessHour, dispatch]),
  });

  /**
   * Handle update business hour
   * @param {object} row Business hour data
   */
  const updateBh = (row) => {
    toggleArchiveBusinessHours();
    setCurrentBusinessHour(row);
  };

  /**
   * Handla edit business hour
   dispatch(ReservationsActions.fetchBusinessHours(_params));
   * @param {object} row Business hour data 
   */
  const handleEdit = (row) => {
    dispatch(BusinessHoursActions.resetCurrentBusinessHour());
    history.push(buildUrl(`/business-hours/${row?.id}/update`));
  };

  /**
   * Handla clone business hour
   * @param {object} row Business hour data
   */
  const handleClone = (row) => {
    dispatch(BusinessHoursActions.resetCurrentBusinessHour());
    history.push(buildUrl(`/business-hours/${row?.id}/clone`));
  };

  /**
   * Table columns
   * Show or hide columns with omit key
   */
  const columns = [
    {
      name: i18n.t('business_hours.business_hour_name'),
      selector: 'name',
      grow: 2,
    },
    {
      name: i18n.t('common.description'),
      selector: 'description',
      cell: (row) => row?.description,
      grow: 3,
    },
    {
      name: i18n.t('common.actions'),
      selector: 'action',
      cell: (row) => {
        return (
          <Box display="flex" justifyContent="center">
            <Tooltip title={i18n.t('business_hours.edit_business_hour')}>
              <IconButton aria-label="edit" size="medium" onClick={() => handleEdit(row)}>
                <EditIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            <Tooltip title={i18n.t('business_hours.clone')}>
              <IconButton aria-label="clone" size="medium" onClick={() => handleClone(row)}>
                <FileCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
            {row.enabled ? (
              <Can I="delete" on="invoice_item_type">
                <Tooltip title={i18n.t('business_hours.archive')}>
                  <IconButton
                    // disabled={fetchingDeleteBusinessHour}
                    aria-label="archive"
                    onClick={() => updateBh(row)}
                  >
                    <ArchiveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Can>
            ) : (
              <Can I="update" on="invoice_item_type">
                <Tooltip title={i18n.t('business_hours.activate')}>
                  <IconButton
                    // disabled={fetchingUpdateBusinessHour}
                    aria-label="unarchive"
                    onClick={() => updateBh(row)}
                  >
                    <UnarchiveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Can>
            )}
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    if (isMounted.current) {
      // On params change once is mounted, debounced
      fetchBusinessHoursDebounced(params);
    } else {
      // Run once on mount, no debounce
      isMounted.current = true;
      fetchBusinessHours(params);
    }
  }, [fetchBusinessHoursDebounced, params]);

  // On refetch delete/update success no debounce
  const refetch = !fetchingUpdateBH && fetchedUpdateBH;
  useEffect(() => {
    if (refetch) {
      fetchBusinessHours(params);
    }
  }, [fetchBusinessHours, refetch]);

  const handleParams = (params) => {
    setParams(params);
    const _params = toQueryString(params);
    history.replace(buildUrl(`/business-hours${_params}`));
  };

  /**
   * Handle page change on table
   * @param {number} page Number of the page
   */
  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle sort change on table
   * @param {string} column Column of the table
   * @param {string} sortDirection Sort direction
   */
  const handleSort = ({ column }) => {
    const newParams = {
      sort_by: column.sortableField,
    };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle per rows (limit) change on table
   * @param {number} limit New limit value
   */
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(buildUrl(`/business-hours/${row?.id}`));
  };

  /**
   * Handle search filter
   * @param {string} name Name of the field to search
   * @param {string} value Value of the input
   */
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach(
      (key) => (!filters[key] || filters[key] === '') && key != 'archived' && delete filters[key]
    );
    handleParams(filters);
  };

  return (
    <Layout title={i18n.t('business_hours.title')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
              <Grid item>
                <Title title={i18n.t('business_hours.title')} />
              </Grid>
              <Grid item>
                <Button
                  onClick={() => {
                    dispatch(BusinessHoursActions.resetCurrentBusinessHour());
                    history.push(buildUrl('/business-hours/create'));
                  }}
                  style={{ textTransform: 'capitalize' }}
                  data-cy="create-bh-ruleset"
                  variant="contained"
                  color="primary"
                >
                  {i18n.t('business_hours.create_button')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="flex-end">
              <Grid item xs={12} sm={4} md={3}>
                <SearchField
                  label={i18n.t('business_hours.search')}
                  field={{
                    onChange: search,
                    name: 'keyword',
                    value: params.keyword || '',
                  }}
                  inputProps={{
                    startAdornment: (
                      <InputAdornment
                        classes={{
                          root: classes.searchIcon,
                        }}
                        position="start"
                      >
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <FormControlLabel
                  value="start"
                  className={classes.switchLabel}
                  control={
                    <Switch
                      onChange={(_, value) => search('archived', value)}
                      checked={params.archived}
                      name="archived"
                      color="primary"
                    />
                  }
                  label={i18n.t('business_hours.include_archived')}
                  labelPlacement="start"
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Paper elevation={2} className={classes.paper}>
              <Table
                data={businessHours}
                columns={columns}
                onSort={handleSort}
                loading={fetchingBusinessHours}
                page={params.page}
                limit={params.limit}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                totalRows={pagination?.totalRows || 0}
                handlePerRowsChange={handlePerRowsChange}
                extraStyles={{ ...tableStyles }}
              />
            </Paper>
          </Grid>
        </Grid>
        <ArchiveBusinessHoursConfirm />
      </Container>
    </Layout>
  );
};

export default BusinessHoursList;
